import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { ArrowUpRight, MapPin, Play } from 'lucide-react';
import { db } from '../../firebase/config';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import { cn } from '../../lib/utils';
import coloradoImage from '../../colorado.webp';
import SEO from '../SEO';
import { SITE, absoluteUrl } from '../../lib/seo';

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
};

const deriveStatus = (t) => {
  const now = new Date();
  const start = toDate(t.startDate);
  const end = toDate(t.endDate);
  if (!start || !end) return t.status || 'upcoming';
  if (now < start) return 'upcoming';
  if (now > end) return 'completed';
  return 'active';
};

const monthAbbrev = (d) =>
  d
    ? d
        .toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
        .toUpperCase()
        .replace(/\./g, '')
    : '—';

const year = (d) => (d ? d.getFullYear() : '—');

const formatLabel = {
  staged: 'TOURNAMENT',
  guess: 'GUESS',
  mapping: 'MAPPING',
  leaderboard: 'LEADERBOARD',
  standard: 'BRACKET',
};

const LandingPage = () => {
  const { config, loading: configLoading } = useSiteConfig();
  const landing = config.landing || {};
  const heroC = landing.hero || {};
  const ledgerC = landing.ledger || {};
  const mapC = landing.mapOfDay || {};
  const youtubeC = landing.youtube || {};
  const communityC = landing.community || {};
  const heroBg = heroC.backgroundImageUrl || (configLoading ? null : coloradoImage);
  const [tournaments, setTournaments] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const q = query(collection(db, 'tournaments'), orderBy('startDate', 'desc'));
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!cancelled) setTournaments(rows);
      } catch {
        // Non-admins may not have permission — degrade gracefully.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const featuredId = ledgerC.featuredTournamentId || '';
  const featured = useMemo(() => {
    const withStatus = tournaments
      .map((t) => ({ ...t, computedStatus: deriveStatus(t) }))
      .filter((t) => t.computedStatus !== 'completed');
    const rank = (s) => ({ active: 0, upcoming: 1 })[s] ?? 3;
    withStatus.sort((a, b) => {
      if (featuredId && a.id === featuredId) return -1;
      if (featuredId && b.id === featuredId) return 1;
      const r = rank(a.computedStatus) - rank(b.computedStatus);
      if (r !== 0) return r;
      const aStart = toDate(a.startDate)?.getTime() ?? 0;
      const bStart = toDate(b.startDate)?.getTime() ?? 0;
      return bStart - aStart;
    });
    return withStatus.slice(0, 5);
  }, [tournaments, featuredId]);

  const liveNow = featured.some((t) => t.computedStatus === 'active');

  const stats = useMemo(() => {
    const active = tournaments.filter((t) => deriveStatus(t) === 'active').length;
    const upcoming = tournaments.filter((t) => deriveStatus(t) === 'upcoming').length;
    const total = tournaments.length;
    const latestStart = tournaments
      .map((t) => toDate(t.startDate))
      .filter(Boolean)
      .sort((a, b) => b - a)[0];
    const season = latestStart ? latestStart.getFullYear() : new Date().getFullYear();
    return { active, upcoming, total, season };
  }, [tournaments]);

  const discord = (config.footer.socials || []).find((s) => s.platform === 'discord' && s.url);

  const landingJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE.name,
      url: absoluteUrl('/'),
      description: SITE.longDescription,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${absoluteUrl('/users')}?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE.name,
      url: absoluteUrl('/'),
      logo: absoluteUrl('/logo512.png'),
    },
  ];

  return (
    <div className="relative isolate">
      <SEO
        title="Competitive KZ Tournaments &amp; Daily Map Guessing"
        description={SITE.longDescription}
        canonicalPath="/"
        jsonLd={landingJsonLd}
      />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border/40">
        <div className="absolute inset-0 -z-10 bg-background">
          {heroBg && (
            <img src={heroBg} alt="" aria-hidden="true" className="h-full w-full object-cover" />
          )}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,hsl(var(--background)/0)_0%,hsl(var(--background)/0.25)_90%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_top_right,hsl(var(--background)/0.75)_0%,hsl(var(--background)/0.35)_45%,hsl(var(--background)/0)_75%)]" />
          <div className="absolute inset-0 topo-lines opacity-25" />
          <div className="absolute inset-0 grain" />
        </div>

        <div className="mx-auto grid min-h-[88vh] max-w-screen-2xl grid-cols-1 items-end gap-16 px-4 pb-16 pt-24 sm:px-6 lg:pb-24 lg:pt-32">
          <div className="flex flex-col gap-10">
            {(heroC.dossierPrefix || heroC.coordinates) && (
              <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                {heroC.dossierPrefix && (
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block h-px w-6 bg-foreground/60" />
                    {heroC.dossierPrefix} {stats.season}
                  </span>
                )}
                {heroC.dossierPrefix && heroC.coordinates && (
                  <span className="hidden text-foreground/30 sm:inline">·</span>
                )}
                {heroC.coordinates && <span className="hidden sm:inline">{heroC.coordinates}</span>}
              </div>
            )}

            {(heroC.headlineLine1 || heroC.headlineLine2) && (
              <h1 className="font-display text-[14vw] font-bold leading-[0.88] tracking-[-0.04em] text-foreground sm:text-[10vw] lg:text-[7.2rem]">
                {heroC.headlineLine1 && <span className="block">{heroC.headlineLine1}</span>}
                {heroC.headlineLine2 && (
                  <span className="block">
                    <span className="text-foreground/30">{heroC.headlineLine2}</span>
                  </span>
                )}
              </h1>
            )}

            {config.hero.tagline && (
              <p className="max-w-xl text-base text-muted-foreground sm:text-lg">
                {config.hero.tagline}
              </p>
            )}

            {((config.hero.primaryCta?.label && config.hero.primaryCta?.path) ||
              (config.hero.secondaryCta?.label && config.hero.secondaryCta?.path)) && (
              <div className="flex flex-wrap items-center gap-4">
                {config.hero.primaryCta?.label && config.hero.primaryCta?.path && (
                  <Link
                    to={config.hero.primaryCta.path}
                    className="group inline-flex items-center gap-2 bg-heat px-6 py-3 font-mono text-sm font-semibold uppercase tracking-[0.2em] text-heat-foreground transition-transform hover:-translate-y-0.5"
                  >
                    {config.hero.primaryCta.label}
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                )}
                {config.hero.secondaryCta?.label && config.hero.secondaryCta?.path && (
                  <Link
                    to={config.hero.secondaryCta.path}
                    className="group inline-flex items-center gap-2 border border-border/80 px-6 py-3 font-mono text-sm font-medium uppercase tracking-[0.2em] text-foreground transition-colors hover:border-foreground hover:bg-foreground/5"
                  >
                    {liveNow && (
                      <span className="relative flex h-1.5 w-1.5 items-center justify-center">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-heat opacity-70" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-heat" />
                      </span>
                    )}
                    {config.hero.secondaryCta.label}
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* LEDGER */}
      {ledgerC.enabled !== false && (!loaded || featured.length > 0) && (
        <section className="border-b border-border/40 bg-background">
          <div className="mx-auto max-w-screen-2xl px-4 py-20 sm:px-6">
            <header className="mb-10 flex items-end justify-between gap-4">
              <div>
                {ledgerC.eyebrow && (
                  <div className="mb-3 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                    <span className="h-px w-6 bg-heat" />
                    {ledgerC.eyebrow}
                  </div>
                )}
                {ledgerC.title && (
                  <h2 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
                    {ledgerC.title}
                  </h2>
                )}
              </div>
              {ledgerC.viewAllLabel && ledgerC.viewAllPath && (
                <Link
                  to={ledgerC.viewAllPath}
                  className="group hidden items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-foreground/80 transition-colors hover:text-foreground sm:inline-flex"
                >
                  {ledgerC.viewAllLabel}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              )}
            </header>

            <ol className="divide-y divide-border/60 border-y border-border/60">
              {featured.map((t, i) => (
                <LedgerRow
                  key={t.id}
                  tournament={t}
                  index={i}
                  isFeatured={featuredId && t.id === featuredId}
                  featuredLabel={ledgerC.featuredBadge || 'Featured'}
                />
              ))}
              {!loaded &&
                Array.from({ length: 3 }).map((_, i) => (
                  <li key={`skel-${i}`} className="flex h-20 animate-pulse items-center gap-8 px-2">
                    <div className="h-4 w-8 bg-muted" />
                    <div className="h-5 w-1/3 bg-muted" />
                    <div className="ml-auto h-4 w-24 bg-muted" />
                  </li>
                ))}
            </ol>

            {ledgerC.viewAllLabel && ledgerC.viewAllPath && (
              <div className="mt-6 sm:hidden">
                <Link
                  to={ledgerC.viewAllPath}
                  className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-foreground/80"
                >
                  {ledgerC.viewAllLabel}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* MAP OF THE DAY */}
      {(mapC.title || mapC.description || mapC.eyebrow || mapC.ctaLabel) && (
        <section className="border-b border-border/40 bg-background">
          <div className="mx-auto max-w-screen-2xl px-4 py-16 sm:px-6">
            {(() => {
              const inner = (
                <>
                  <div className="absolute inset-0 -z-10 topo-lines opacity-40" />
                  <div className="grid gap-6 p-8 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:gap-10">
                    <div className="flex h-14 w-14 items-center justify-center border border-heat/50 bg-heat/10 text-heat">
                      <MapPin className="h-6 w-6" />
                    </div>
                    <div>
                      {mapC.eyebrow && (
                        <div className="mb-2 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                          <span className="h-px w-6 bg-heat" />
                          {mapC.eyebrow}
                        </div>
                      )}
                      {mapC.title && (
                        <h3 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                          {mapC.title}
                        </h3>
                      )}
                      {mapC.description && (
                        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                          {mapC.description}
                        </p>
                      )}
                    </div>
                    {mapC.ctaLabel && mapC.ctaPath && (
                      <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-foreground/80 group-hover:text-foreground">
                        {mapC.ctaLabel}
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    )}
                  </div>
                </>
              );
              return mapC.ctaPath ? (
                <Link
                  to={mapC.ctaPath}
                  className="group relative block overflow-hidden border border-border/60 bg-background/40 transition-colors hover:border-heat/60"
                >
                  {inner}
                </Link>
              ) : (
                <div className="relative block overflow-hidden border border-border/60 bg-background/40">
                  {inner}
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {/* YOUTUBE */}
      {youtubeC.enabled &&
        (youtubeC.title || youtubeC.description || youtubeC.eyebrow || youtubeC.ctaLabel) && (
          <section className="border-b border-border/40 bg-background">
            <div className="mx-auto max-w-screen-2xl px-4 py-16 sm:px-6">
              {(() => {
                const inner = (
                  <>
                    <div className="absolute inset-0 -z-10 topo-lines opacity-40" />
                    <div className="grid gap-6 p-8 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:gap-10">
                      <div className="flex h-14 w-14 items-center justify-center border border-heat/50 bg-heat/10 text-heat">
                        <Play className="h-6 w-6" />
                      </div>
                      <div>
                        {youtubeC.eyebrow && (
                          <div className="mb-2 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                            <span className="h-px w-6 bg-heat" />
                            {youtubeC.eyebrow}
                          </div>
                        )}
                        {youtubeC.title && (
                          <h3 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                            {youtubeC.title}
                          </h3>
                        )}
                        {youtubeC.description && (
                          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                            {youtubeC.description}
                          </p>
                        )}
                      </div>
                      {youtubeC.ctaLabel && youtubeC.ctaPath && (
                        <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-foreground/80 group-hover:text-foreground">
                          {youtubeC.ctaLabel}
                          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </span>
                      )}
                    </div>
                  </>
                );
                if (!youtubeC.ctaPath) {
                  return (
                    <div className="relative block overflow-hidden border border-border/60 bg-background/40">
                      {inner}
                    </div>
                  );
                }
                const external = /^(https?:|\/\/)/i.test(youtubeC.ctaPath);
                return external ? (
                  <a
                    href={youtubeC.ctaPath}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="group relative block overflow-hidden border border-border/60 bg-background/40 transition-colors hover:border-heat/60"
                  >
                    {inner}
                  </a>
                ) : (
                  <Link
                    to={youtubeC.ctaPath}
                    className="group relative block overflow-hidden border border-border/60 bg-background/40 transition-colors hover:border-heat/60"
                  >
                    {inner}
                  </Link>
                );
              })()}
            </div>
          </section>
        )}

      {/* COMMUNITY */}
      {(communityC.eyebrow ||
        communityC.headlineLine1 ||
        communityC.headlineLine2 ||
        communityC.description ||
        (discord && communityC.discordLabel) ||
        (communityC.browseLabel && communityC.browsePath)) && (
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 -z-10 topo-lines opacity-40" />
          <div className="mx-auto grid max-w-screen-2xl grid-cols-1 gap-12 px-4 py-24 sm:px-6 lg:grid-cols-2 lg:items-end">
            <div>
              {communityC.eyebrow && (
                <div className="mb-4 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                  <span className="h-px w-6 bg-heat" />
                  {communityC.eyebrow}
                </div>
              )}
              {(communityC.headlineLine1 || communityC.headlineLine2) && (
                <h2 className="font-display text-5xl font-bold leading-[0.95] tracking-tight sm:text-6xl">
                  {communityC.headlineLine1}
                  {communityC.headlineLine1 && communityC.headlineLine2 && <br />}
                  {communityC.headlineLine2 && (
                    <span className="text-foreground/30">{communityC.headlineLine2}</span>
                  )}
                </h2>
              )}
            </div>
            <div className="flex flex-col items-start gap-5 lg:items-end">
              {communityC.description && (
                <p className="max-w-md text-base text-muted-foreground lg:text-right">
                  {communityC.description}
                </p>
              )}
              {((discord && communityC.discordLabel) ||
                (communityC.browseLabel && communityC.browsePath)) && (
                <div className="flex flex-wrap items-center gap-3 lg:justify-end">
                  {discord && communityC.discordLabel && (
                    <a
                      href={discord.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="group inline-flex items-center gap-2 bg-foreground px-6 py-3 font-mono text-sm font-semibold uppercase tracking-[0.2em] text-background transition-transform hover:-translate-y-0.5"
                    >
                      {communityC.discordLabel}
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  )}
                  {communityC.browseLabel && communityC.browsePath && (
                    <Link
                      to={communityC.browsePath}
                      className="group inline-flex items-center gap-2 border border-border/80 px-6 py-3 font-mono text-sm font-medium uppercase tracking-[0.2em] text-foreground transition-colors hover:border-foreground hover:bg-foreground/5"
                    >
                      {communityC.browseLabel}
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

const statusPalette = {
  active: 'border-heat/60 text-heat',
  upcoming: 'border-border text-foreground',
  completed: 'border-border/60 text-muted-foreground',
};

const LedgerRow = ({ tournament, index, isFeatured, featuredLabel }) => {
  const start = toDate(tournament.startDate);
  const end = toDate(tournament.endDate);
  const status = tournament.computedStatus;
  const format = formatLabel[tournament.format] || (tournament.format || 'EVENT').toUpperCase();

  return (
    <li className={cn('group', isFeatured && 'bg-heat/[0.04]')}>
      <Link
        to={`/tournaments/${tournament.slug || tournament.id}`}
        className="grid grid-cols-[auto_1fr_auto] items-center gap-4 px-2 py-5 transition-colors hover:bg-foreground/[0.03] sm:grid-cols-[auto_1fr_auto_auto_auto] sm:gap-8"
      >
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
          {String(index + 1).padStart(2, '0')}
        </span>

        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <div className="truncate font-display text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
              {tournament.name || 'Untitled tournament'}
            </div>
            {isFeatured && featuredLabel && (
              <span className="shrink-0 border border-heat/60 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.25em] text-heat">
                {featuredLabel}
              </span>
            )}
          </div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            {format} · {year(start)}
          </div>
        </div>

        <div className="hidden font-mono text-xs uppercase tracking-[0.2em] text-foreground/70 tabular-nums sm:block">
          {monthAbbrev(start)}
          <span className="mx-2 text-foreground/30">→</span>
          {monthAbbrev(end)}
        </div>

        <span
          className={cn(
            'hidden border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.25em] sm:inline-flex',
            statusPalette[status] || statusPalette.upcoming
          )}
        >
          {status === 'active' && (
            <span className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse-live rounded-full bg-heat align-middle" />
          )}
          {status}
        </span>

        <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-foreground" />
      </Link>
    </li>
  );
};

export default LandingPage;
